import { Toast } from './Toast.js';
import { writeTextToClipboard } from './clipboard.js';

/**
 * Nav — fixed top navigation bar with glass-morphism effect.
 *
 * Actions row (signed-in): Share | Export (split: PDF primary, PNG dropdown) | Avatar
 */
export class Nav {
  constructor({ onExportPDF, onExportPNG, onPublishToggle, onSignOut, user } = {}) {
    this._onExportPDF = onExportPDF;
    this._onExportPNG = onExportPNG;
    this._onPublishToggle = onPublishToggle;
    this._onSignOut = onSignOut;
    this._user = user || null;
    this._resume = null;
    this._el = null;
    this._mobileOpen = false;
  }

  render(container) {
    const nav = document.createElement('nav');
    nav.className = 'site-nav';
    nav.innerHTML = this._template();
    container.prepend(nav);
    this._el = nav;
    this._bindEvents();
  }

  updateUser(user) {
    this._user = user || null;
    if (!this._el) return;

    const avatarBtn = this._el.querySelector('.nav-avatar-btn');
    if (avatarBtn) avatarBtn.innerHTML = this._avatarContent();

    const avatarWrap = this._el.querySelector('.nav-avatar-wrap');
    if (avatarWrap) avatarWrap.style.display = user ? '' : 'none';

    const exportWrap = this._el.querySelector('.nav-export-wrap');
    if (exportWrap) exportWrap.style.display = user ? '' : 'none';

    const shareBtn = this._el.querySelector('.nav-share-btn');
    if (shareBtn) shareBtn.style.display = user ? '' : 'none';
  }

  updateResume(resume) {
    this._resume = resume;
    this._renderShareButton();
  }

  _getInitials() {
    if (!this._user) return '?';
    const name = this._user.displayName || this._user.email || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (name[0] || '?').toUpperCase();
  }

  _avatarContent() {
    if (this._user?.photoURL) {
      return `<img src="${this._user.photoURL}" alt="Avatar" class="nav-avatar-img" />`;
    }
    return `<span class="nav-avatar-initials">${this._getInitials()}</span>`;
  }

  _template() {
    const hidden = this._user ? '' : 'display:none';
    return `
      <div class="nav-inner">
        <a href="/" class="nav-logo">
          <img src="/assets/icon.png" alt="The Running Genie" />
          <span>The Running Genie</span>
        </a>

        <button class="nav-toggle" aria-label="Toggle menu">
          <span></span><span></span><span></span>
        </button>

        <ul class="nav-links">
          <li><a href="/">Home</a></li>
          <li class="nav-dropdown">
            <button class="nav-dropdown-toggle" aria-expanded="false" aria-haspopup="true">Tools</button>
            <ul class="nav-dropdown-menu">
              <li><a href="/resume">My Running Resume</a></li>
              <li><a href="/verify-your-run">Verify Your Run</a></li>
            </ul>
          </li>
        </ul>

        <div class="nav-actions">
          <button class="nav-share-btn" type="button" style="${hidden}">Share</button>

          <div class="nav-export-wrap" style="${hidden}">
            <button class="nav-export-btn" type="button">Export PDF</button>
            <button class="nav-export-chevron" type="button" aria-label="Export options">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <div class="nav-export-dropdown" aria-hidden="true">
              <button class="nav-export-dropdown-item" data-format="pdf" type="button">Export PDF</button>
              <button class="nav-export-dropdown-item" data-format="png" type="button">Export PNG</button>
            </div>
          </div>

          <div class="nav-avatar-wrap" style="${hidden}">
            <button class="nav-avatar-btn" type="button" aria-label="User menu">
              ${this._avatarContent()}
            </button>
            <div class="nav-avatar-dropdown" aria-hidden="true">
              <button class="nav-avatar-dropdown-item" type="button">Sign Out</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  _renderShareButton() {
    const btn = this._el?.querySelector('.nav-share-btn');
    if (!btn) return;

    const resume = this._resume;
    const isPublished = resume?.isPublished ?? false;

    if (isPublished && resume?.id) {
      btn.textContent = '';
      btn.classList.add('nav-share-btn--published');

      const icon = document.createElement('span');
      icon.className = 'nav-share-icon';
      icon.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>`;
      btn.appendChild(icon);

      const urlText = document.createElement('span');
      urlText.className = 'nav-share-url';
      urlText.textContent = `therunninggenie.com/r/${resume.id}`;
      btn.appendChild(urlText);

      btn.onclick = async () => {
        try {
          await writeTextToClipboard(`https://therunninggenie.com/r/${resume.id}`);
          Toast.show('Link copied!', 'success');
        } catch {
          Toast.show('Failed to copy link.', 'error');
        }
      };
    } else {
      btn.textContent = 'Share';
      btn.classList.remove('nav-share-btn--published');
      btn.onclick = () => this._onPublishToggle?.();
    }
  }

  _bindEvents() {
    const nav = this._el;

    const exportBtn = nav.querySelector('.nav-export-btn');
    if (exportBtn) {
      exportBtn.addEventListener('click', () => this._onExportPDF?.());
    }

    const chevron = nav.querySelector('.nav-export-chevron');
    const exportDropdown = nav.querySelector('.nav-export-dropdown');
    if (chevron && exportDropdown) {
      chevron.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = exportDropdown.classList.toggle('open');
        exportDropdown.setAttribute('aria-hidden', String(!isOpen));
      });
    }

    nav.querySelectorAll('.nav-export-dropdown-item').forEach((item) => {
      item.addEventListener('click', () => {
        exportDropdown?.classList.remove('open');
        exportDropdown?.setAttribute('aria-hidden', 'true');
        if (item.dataset.format === 'png') {
          this._onExportPNG?.();
        } else {
          this._onExportPDF?.();
        }
      });
    });

    const avatarBtn = nav.querySelector('.nav-avatar-btn');
    const avatarDropdown = nav.querySelector('.nav-avatar-dropdown');
    if (avatarBtn && avatarDropdown) {
      avatarBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = avatarDropdown.classList.toggle('open');
        avatarDropdown.setAttribute('aria-hidden', String(!isOpen));
      });
    }

    const signOutBtn = nav.querySelector('.nav-avatar-dropdown-item');
    if (signOutBtn) {
      signOutBtn.addEventListener('click', () => {
        avatarDropdown?.classList.remove('open');
        avatarDropdown?.setAttribute('aria-hidden', 'true');
        this._onSignOut?.();
      });
    }

    document.addEventListener('click', () => {
      if (avatarDropdown?.classList.contains('open')) {
        avatarDropdown.classList.remove('open');
        avatarDropdown.setAttribute('aria-hidden', 'true');
      }
      if (exportDropdown?.classList.contains('open')) {
        exportDropdown.classList.remove('open');
        exportDropdown.setAttribute('aria-hidden', 'true');
      }
    });

    const toolsDropdown = nav.querySelector('.nav-dropdown');
    const toolsToggle = nav.querySelector('.nav-dropdown-toggle');
    if (toolsDropdown && toolsToggle) {
      toolsToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = toolsDropdown.classList.toggle('open');
        toolsToggle.setAttribute('aria-expanded', String(isOpen));
      });
      document.addEventListener('click', () => {
        toolsDropdown.classList.remove('open');
        toolsToggle.setAttribute('aria-expanded', 'false');
      });
    }

    const toggle = nav.querySelector('.nav-toggle');
    const links = nav.querySelector('.nav-links');
    if (toggle && links) {
      toggle.addEventListener('click', () => {
        this._mobileOpen = !this._mobileOpen;
        toggle.classList.toggle('open', this._mobileOpen);
        links.classList.toggle('open', this._mobileOpen);
      });
      links.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          this._mobileOpen = false;
          toggle.classList.remove('open');
          links.classList.remove('open');
        });
      });
    }
  }
}
